import { useState, useEffect } from 'react';
import { UI_CONFIG, ERROR_MESSAGES } from '../config';
import styles from './EventForm.module.css';
import { IconChevronLeft, IconChevronRight, IconSend, IconInfoCircle, IconPhotoPlus } from '@tabler/icons-react';
import { organizationService } from '../services/api';
import { categoryService } from '../services/categoryService';
import AddressAutocomplete from './AddressAutocomplete';
import { Coordinates } from '../types/event';

interface Organization {
  id: number;
  name: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

interface EventFormData {
  title: string;
  description: string;
  dateTime: string;
  location: string;
  coordinates?: Coordinates;
  category: string;
  subcategory?: string;
  tags: string;
  maxParticipants?: number;
  organizationId: number;
  imageUrl?: string;
}

interface EventFormProps {
  onSubmit: (data: EventFormData) => Promise<any>;
  onStepChange?: (step: number) => void;
  currentStep?: number;
  preselectedOrganizationId?: number | null;
  formData?: EventFormData;
  onFormDataChange?: (data: EventFormData) => void;
}

export function EventForm({ 
  onSubmit, 
  onStepChange = () => {}, 
  currentStep = 1,
  preselectedOrganizationId = null,
  formData: externalFormData,
  onFormDataChange = () => {}
}: EventFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState(currentStep);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  
  // Formulaire en plusieurs étapes
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    dateTime: '',
    location: '',
    coordinates: undefined,
    category: 'other', // default category
    subcategory: '',
    tags: '',
    maxParticipants: undefined,
    organizationId: preselectedOrganizationId || 0,
    imageUrl: ''
  });

  useEffect(() => {
    setStep(currentStep);
  }, [currentStep]);

  // Use external form data if provided
  useEffect(() => {
    if (externalFormData) {
      setFormData(prev => ({
        ...prev,
        ...externalFormData
      }));
    }
  }, [externalFormData]);

  // Update external form data when local form data changes
  useEffect(() => {
    onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  // Chargement des organisations de l'utilisateur
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setError(null);
        const userOrgs = await organizationService.getUserOrganizations();
        
        // Vérifier si l'utilisateur a des organisations
        if (!userOrgs || userOrgs.length === 0) {
          setError('Vous devez créer ou rejoindre une organisation avant de pouvoir créer un événement.');
          setOrganizations([]);
          return;
        }
        
        setOrganizations(userOrgs);
        
        // Si une organisation est présélectionnée, vérifier qu'elle fait partie des organisations de l'utilisateur
        if (preselectedOrganizationId) {
          const orgExists = userOrgs.some(org => org.id === preselectedOrganizationId);
          if (orgExists) {
            setFormData(prev => ({ ...prev, organizationId: preselectedOrganizationId }));
          } else {
            // Si l'organisation présélectionnée n'est pas trouvée, utiliser la première organisation disponible
            if (userOrgs.length > 0) {
              setFormData(prev => ({ ...prev, organizationId: userOrgs[0].id }));
            }
            console.warn(`L'organisation présélectionnée (ID: ${preselectedOrganizationId}) n'est pas disponible pour cet utilisateur`);
          }
        } else if (userOrgs.length > 0) {
          // Si aucune organisation n'est présélectionnée, utiliser la première organisation disponible
          setFormData(prev => ({ ...prev, organizationId: userOrgs[0].id }));
        }
      } catch (err) {
        console.error('Erreur lors du chargement des organisations:', err);
        setError('Impossible de charger vos organisations. Veuillez réessayer ou contacter un administrateur.');
        setOrganizations([]);
      }
    };

    fetchOrganizations();
  }, [preselectedOrganizationId]);

  // Charger les catégories d'événements
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const options = await categoryService.getCategoriesAsOptions();
        setCategoryOptions(options);
        
        // Si aucune catégorie n'est sélectionnée, utiliser la première par défaut
        if (!formData.category && options.length > 0) {
          setFormData(prev => ({ ...prev, category: options[0].value }));
        }
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err);
        // En cas d'erreur, utiliser les catégories par défaut
        setError('Impossible de charger les catégories. Les catégories par défaut seront utilisées.');
      }
    };

    fetchCategories();
  }, []);

  const validateBasicInfo = (): string | null => {
    if (!formData.title.trim()) {
      return 'Le titre est requis';
    }
    if (!formData.description.trim() || formData.description.length < 20) {
      return 'Une description d\'au moins 20 caractères est requise';
    }
    return null;
  };

  const validateLocationAndTime = (): string | null => {
    if (!formData.location.trim()) {
      return 'Le lieu est requis';
    }
    if (!formData.dateTime) {
      return 'La date est requise';
    }
    if (new Date(formData.dateTime) < new Date()) {
      return 'La date doit être dans le futur';
    }
    return null;
  };

  const validateDetails = (): string | null => {
    if (formData.maxParticipants && formData.maxParticipants < 1) {
      return 'Le nombre de participants doit être positif';
    }
    return null;
  };

  const validateCurrentStep = (): string | null => {
    switch (step) {
      case 1:
        return validateBasicInfo();
      case 2:
        return validateLocationAndTime();
      case 3:
        return validateDetails();
      default:
        return null;
    }
  };

  const validateForm = (): string | null => {
    const basicInfoError = validateBasicInfo();
    if (basicInfoError) return basicInfoError;
    
    const locationTimeError = validateLocationAndTime();
    if (locationTimeError) return locationTimeError;
    
    const detailsError = validateDetails();
    if (detailsError) return detailsError;
    
    return null;
  };

  const handleNextStep = () => {
    const validationError = validateCurrentStep();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const nextStep = step + 1;
    setStep(nextStep);
    onStepChange(nextStep);
  };

  const handlePrevStep = () => {
    const prevStep = step - 1;
    setStep(prevStep);
    onStepChange(prevStep);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Vérifier si l'utilisateur a des organisations
      if (organizations.length === 0) {
        setError("Vous devez être membre d'au moins une organisation pour créer un événement.");
        setLoading(false);
        return;
      }
      
      // Créer une copie des données du formulaire
      const formDataToSubmit = {
        ...formData,
        // S'assurer que organizationId est bien un entier
        organizationId: parseInt(formData.organizationId.toString(), 10)
      };

      // Vérifier si organizationId est un nombre valide
      if (isNaN(formDataToSubmit.organizationId)) {
        setError("ID d'organisation invalide");
        setLoading(false);
        return;
      }

      // Appeler la fonction onSubmit et retourner le résultat
      // sans faire de redirection (la redirection est gérée par la page parent)
      const event = await onSubmit(formDataToSubmit);
      return event;
    } catch (err) {
      console.error('Error creating event:', err);
      setError(err instanceof Error ? err.message : ERROR_MESSAGES.DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError(null);
    onFormDataChange({ ...formData, [name]: value });
  };

  const handleAddressChange = (address: string) => {
    setFormData({ ...formData, location: address });
    setError(null);
    onFormDataChange({ ...formData, location: address });
  };

  const handleCoordinatesChange = (coordinates: Coordinates | null) => {
    setFormData({ ...formData, coordinates: coordinates || undefined });
    onFormDataChange({ ...formData, coordinates: coordinates || undefined });
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className={styles.formStep}>
            <h2>Informations de base</h2>
            <p className={styles.stepDescription}>Commencez par les informations essentielles de votre événement.</p>
            
            <div className={styles.formGroup}>
              <label htmlFor="title" className={styles.required}>Titre</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                maxLength={100}
                placeholder="Ex: Soirée d'intégration des nouveaux étudiants"
                required
                className={styles.formControl}
              />
              <span className={styles.fieldHint}>Un titre court et accrocheur (100 caractères max)</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.required}>Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                maxLength={UI_CONFIG.MAX_DESCRIPTION_LENGTH}
                rows={6}
                placeholder="Décrivez votre événement, son contenu, son but..."
                className={styles.formControl}
              />
              <div className={styles.textareaStats}>
                <span className={styles.fieldHint}>Description détaillée de votre événement</span>
                <span className={styles.characterCount}>
                  {formData.description.length}/{UI_CONFIG.MAX_DESCRIPTION_LENGTH}
                </span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="category" className={styles.required}>Catégorie</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className={styles.formControl}
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {organizations.length > 0 && (
              <div className={styles.formGroup}>
                <label htmlFor="organizationId" className={styles.required}>Organisation</label>
                <select
                  id="organizationId"
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleChange}
                  required
                  className={styles.formControl}
                >
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.nextButton}
                onClick={handleNextStep}
              >
                Étape suivante <IconChevronRight size={18} />
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className={styles.formStep}>
            <h2>Lieu et horaire</h2>
            <p className={styles.stepDescription}>Précisez où et quand aura lieu votre événement.</p>
            
            <div className={styles.formGroup}>
              <label htmlFor="location" className={styles.required}>Lieu</label>
              <AddressAutocomplete
                inputId="location"
                value={formData.location}
                onChange={handleAddressChange}
                onCoordinatesChange={handleCoordinatesChange}
                placeholder="Entrez l'adresse de l'événement"
                required
                country="ca"
                label=""
              />
              <span className={styles.fieldHint}>Soyez précis pour faciliter l'accès</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="dateTime" className={styles.required}>Date et heure</label>
              <input
                type="datetime-local"
                id="dateTime"
                name="dateTime"
                value={formData.dateTime}
                onChange={handleChange}
                min={new Date().toISOString().slice(0, 16)}
                required
                className={styles.formControl}
              />
              <span className={styles.fieldHint}>La date et l'heure de début de l'événement</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="maxParticipants">Nombre maximum de participants</label>
              <input
                type="number"
                id="maxParticipants"
                name="maxParticipants"
                value={formData.maxParticipants || ''}
                onChange={handleChange}
                min="1"
                placeholder="Illimité si vide"
                className={styles.formControl}
              />
              <span className={styles.fieldHint}>Laissez vide pour un nombre illimité</span>
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handlePrevStep}
              >
                <IconChevronLeft size={18} /> Retour
              </button>
              <button
                type="button"
                className={styles.nextButton}
                onClick={handleNextStep}
              >
                Étape suivante <IconChevronRight size={18} />
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className={styles.formStep}>
            <h2>Détails et publication</h2>
            <p className={styles.stepDescription}>Ajoutez les derniers détails pour finaliser votre événement.</p>
            
            <div className={styles.formGroup}>
              <label htmlFor="subcategory">Sous-catégorie</label>
              <input
                type="text"
                id="subcategory"
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                placeholder="Ex: Conférence, Workshop, Fête..."
                className={styles.formControl}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="tags">Tags (séparés par des virgules)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Ex: tech, design, musique, networking"
                className={styles.formControl}
              />
              <span className={styles.fieldHint}>Aide à la découverte de votre événement</span>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="imageUrl">
                URL de l'image
                <button 
                  type="button" 
                  className={styles.infoButton}
                  onClick={() => {
                    // Ouvrir une boîte de dialogue ou un popover avec les instructions
                    const modal = document.getElementById('imageHelpModal');
                    if (modal) modal.style.display = 'flex';
                  }}
                  title="Comment trouver une URL d'image"
                >
                  <IconInfoCircle size={16} />
                </button>
              </label>
              <div className={styles.inputWithIcon}>
                <IconPhotoPlus size={20} className={styles.inputIcon} />
                <input
                  type="url"
                  id="imageUrl"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  placeholder="https://exemple.com/image.jpg"
                  className={styles.formControl}
                />
              </div>
              <span className={styles.fieldHint}>Une image attractive augmente l'engagement</span>
            </div>

            {/* Modal pour l'aide à l'ajout d'images */}
            <div id="imageHelpModal" className={styles.helpModal}>
              <div className={styles.helpModalContent}>
                <div className={styles.helpModalHeader}>
                  <h3>Comment trouver une URL d'image</h3>
                  <button 
                    type="button" 
                    className={styles.closeModalButton}
                    onClick={() => {
                      const modal = document.getElementById('imageHelpModal');
                      if (modal) modal.style.display = 'none';
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className={styles.helpModalBody}>
                  <h4>Option 1: Utiliser une image depuis le web</h4>
                  <ol>
                    <li>Recherchez une image sur Google, Unsplash, Pexels, etc.</li>
                    <li>Faites un clic droit sur l'image et sélectionnez "Copier l'adresse de l'image"</li>
                    <li>Collez cette URL dans le champ "URL de l'image"</li>
                  </ol>
                  
                  <h4>Option 2: Héberger votre propre image</h4>
                  <ol>
                    <li>Téléchargez votre image sur un service comme <a href="https://imgur.com" target="_blank" rel="noopener noreferrer">Imgur</a> ou <a href="https://postimages.org" target="_blank" rel="noopener noreferrer">PostImages</a></li>
                    <li>Après le téléchargement, copiez le lien direct vers l'image</li>
                    <li>Collez ce lien dans le champ "URL de l'image"</li>
                  </ol>
                  
                  <div className={styles.warningNote}>
                    <strong>Important:</strong> Assurez-vous d'utiliser des images libres de droits ou dont vous possédez les droits.
                  </div>
                </div>
              </div>
            </div>

            {formData.imageUrl && (
              <div className={styles.imagePreview}>
                <img src={formData.imageUrl} alt="Preview" />
              </div>
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.backButton}
                onClick={handlePrevStep}
              >
                <IconChevronLeft size={18} /> Retour
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading}
              >
                {loading ? 'Création en cours...' : 'Publier l\'événement'} {!loading && <IconSend size={18} />}
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={styles.eventFormContainer}>
      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>
            <strong>Erreur:</strong> {error}
            {error.includes('organisation') && (
              <div className={styles.errorHelp}>
                <p>Pour créer un événement, vous devez:</p>
                <ol>
                  <li>Créer ou rejoindre une organisation</li>
                  <li>Être approuvé en tant que membre de l'organisation</li>
                </ol>
                <a href="/organization" className={styles.actionLink}>
                  Gérer mes organisations
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <form className={styles.form} onSubmit={handleSubmit}>
        {renderStepContent()}
      </form>
    </div>
  );
}
